import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Bell, BellOff, Clock, Settings } from 'lucide-react'
import { useNotifications } from '../hooks/useNotifications'
import { Button } from './ui/button'

export default function NotificationSettings() {
  const { permission, requestPermission, scheduleReminder, clearReminders } = useNotifications()
  const [reminderTime, setReminderTime] = useState('20:00')
  const [remindersEnabled, setRemindersEnabled] = useState(false)

  useEffect(() => {
    // Load saved settings
    const savedTime = localStorage.getItem('reminder-time')
    const savedEnabled = localStorage.getItem('reminders-enabled') === 'true'
    
    if (savedTime) setReminderTime(savedTime)
    setRemindersEnabled(savedEnabled && permission === 'granted')
  }, [permission])

  const handleEnableNotifications = async () => {
    const result = await requestPermission()
    if (result === 'granted') {
      setRemindersEnabled(true)
      localStorage.setItem('reminders-enabled', 'true')
      scheduleReminder(reminderTime)
    }
  }

  const handleDisableNotifications = () => {
    setRemindersEnabled(false)
    localStorage.setItem('reminders-enabled', 'false')
    clearReminders()
  }

  const handleTimeChange = (time: string) => {
    setReminderTime(time)
    localStorage.setItem('reminder-time', time)
    
    if (remindersEnabled) {
      clearReminders()
      scheduleReminder(time)
    }
  }

  return (
    <motion.div 
      className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-gray-200"
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
    >
      <div className="flex items-center space-x-3 mb-4">
        <Settings className="w-6 h-6 text-purple-600" />
        <h3 className="text-xl font-bold text-gray-800">Notifiche</h3>
      </div>

      <div className="space-y-4">
        {/* Permission Status */}
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-3">
            {permission === 'granted' ? (
              <Bell className="w-5 h-5 text-purple-600" />
            ) : (
              <BellOff className="w-5 h-5 text-gray-400" />
            )}
            <div>
              <div className="font-medium text-gray-800">
                Stato Notifiche
              </div>
              <div className="text-sm text-gray-600">
                {permission === 'granted' && 'Autorizzate'}
                {permission === 'denied' && 'Bloccate'}
                {permission === 'default' && 'Non richieste'}
              </div>
            </div>
          </div>
          
          {permission !== 'granted' && (
            <Button
              onClick={handleEnableNotifications}
              className="bg-purple-600 hover:bg-purple-700"
              size="sm"
            >
              Abilita
            </Button>
          )}
        </div>

        {/* Reminder Settings */}
        {permission === 'granted' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <Clock className="w-5 h-5 text-purple-600" />
                <div>
                  <div className="font-medium text-gray-800">
                    Promemoria Quotidiano
                  </div>
                  <div className="text-sm text-gray-600">
                    Ti ricorderemo di tracciare il tuo umore
                  </div>
                </div>
              </div>
              
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={remindersEnabled}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setRemindersEnabled(true)
                      localStorage.setItem('reminders-enabled', 'true')
                      scheduleReminder(reminderTime)
                    } else {
                      handleDisableNotifications()
                    }
                  }}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
              </label>
            </div>

            {remindersEnabled && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                className="p-3 bg-purple-50 rounded-lg"
              >
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Orario Promemoria
                </label>
                <input
                  type="time"
                  value={reminderTime}
                  onChange={(e) => handleTimeChange(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg bg-white"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Riceverai una notifica ogni giorno a quest'ora se non hai ancora tracciato il tuo umore
                </p>
              </motion.div>
            )}
          </div>
        )}

        {/* Tips */}
        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="font-medium text-blue-800 mb-2">💡 Suggerimenti</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Le notifiche ti aiutano a mantenere l'abitudine</li>
            <li>• Puoi sempre disabilitarle dalle impostazioni del browser</li>
            <li>• I promemoria vengono inviati solo se non hai tracciato l'umore</li>
          </ul>
        </div>
      </div>
    </motion.div>
  )
}